import type {
  GeoJsonFeatureCollection,
  MapCloudPoint,
  MapPointCloud,
} from '@gpx-spa/geo-domain';
import type { DecodedShipmentTrack } from '@/utils/shipment-detail-map-decode';
import { buildConvoyTrackGraph } from '@/map/operational/convoy/track-graph/build-track-graph';
import { deviceTracksFromDecodedRows } from '@/map/operational/convoy/track-graph/from-decoded';
import { convoyTrackSegmentsToFeatureCollection } from '@/map/operational/convoy/track-graph/to-geojson';
import type { ConvoyTrackSegment } from '@/map/operational/convoy/track-graph/types';
import { lineColorHexForShipmentDevice } from '@/map/operational/shipment-status-line-colors';
import { positiveTrackAccuracyRadiusMeters } from '@/map/operational/build-track-point-cloud';
import type { ShipmentTrackVertexPayload } from '@/map/operational/shipment-track-vertex-payload';

export type TrackDisplayDecodedRow = {
  device_id: number;
  decoded: DecodedShipmentTrack | null;
};

/** One vertex chosen for map path + point cloud (after convoy). */
export type DisplayTrackVertex = {
  lat: number;
  lng: number;
  timestampSec: number;
  battery: number | null;
  fixTypeCode: number | null;
  accuracyMeters: number | null;
  colorIndex: number;
  segmentId: string;
};

export type ShipmentTrackDisplay = {
  vertices: DisplayTrackVertex[];
  /** Ready for MapLayer.setGeoJSON */
  lineCollection: GeoJsonFeatureCollection;
  /** Full cloud before engine zoom thin — same positions as line vertices */
  pointCloud: MapPointCloud;
};

function displayVerticesFromSegments(
  segments: readonly ConvoyTrackSegment[],
): DisplayTrackVertex[] {
  const out: DisplayTrackVertex[] = [];
  for (const seg of segments) {
    for (const v of seg.vertices) {
      out.push({
        lat: v.lat,
        lng: v.lng,
        timestampSec: v.timestampSec,
        battery: v.battery,
        fixTypeCode: v.fixTypeCode,
        accuracyMeters: v.accuracyMeters,
        colorIndex: seg.colorIndex,
        segmentId: seg.id,
      });
    }
  }
  return out;
}

function pointCloudFromDisplayVertices(
  vertices: readonly DisplayTrackVertex[],
  status: string | undefined,
): MapPointCloud {
  const points: MapCloudPoint[] = vertices.map((v) => {
    const payload: ShipmentTrackVertexPayload = {
      timestampSec: v.timestampSec,
      battery: v.battery,
      fixTypeCode: v.fixTypeCode,
      accuracyMeters: v.accuracyMeters,
      segmentId: v.segmentId,
    };
    return {
      position: { lat: v.lat, lng: v.lng },
      style: {
        fillColor: lineColorHexForShipmentDevice(status, v.colorIndex),
        borderColor: '#ffffff',
        radius: 6,
        borderWidth: 2,
      },
      accuracyRadiusMeters: positiveTrackAccuracyRadiusMeters(v.accuracyMeters),
      payload,
    };
  });
  return { points };
}

/**
 * Single display pipeline: convoy graph → line GeoJSON + point cloud from the same vertices.
 */
export function buildShipmentTrackDisplay(
  decodedRows: readonly TrackDisplayDecodedRow[],
  status: string | undefined,
): ShipmentTrackDisplay {
  const tracks = deviceTracksFromDecodedRows(decodedRows);
  const segments = buildConvoyTrackGraph(tracks);
  const vertices = displayVerticesFromSegments(segments);
  return {
    vertices,
    lineCollection: convoyTrackSegmentsToFeatureCollection(segments, status),
    pointCloud: pointCloudFromDisplayVertices(vertices, status),
  };
}

/** Merge line collections from several shipments (operational batch). */
export function mergeShipmentTrackLineCollections(
  collections: readonly GeoJsonFeatureCollection[],
): GeoJsonFeatureCollection {
  const features: GeoJsonFeatureCollection['features'] = [];
  for (const c of collections) {
    features.push(...c.features);
  }
  return { type: 'FeatureCollection', features };
}

/** Merge point clouds from several shipments. */
export function mergeShipmentTrackPointClouds(
  clouds: readonly MapPointCloud[],
): MapPointCloud {
  const points: MapCloudPoint[] = [];
  for (const c of clouds) {
    points.push(...c.points);
  }
  return { points };
}
